import { unwrap, type IndustryTemplateKey, type SetupDraftIn, type WorkspaceOut, type WorkspaceSummary } from "@sagot/api-client";
import { ONBOARDING_STEPS } from "@sagot/constants";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useSearchParams } from "react-router";
import { z } from "zod";
import { setActiveWorkspaceId } from "../app/api";
import { queryKeys } from "../lib/query-keys";
import { useApi } from "../session/session-provider";
import { useMe } from "../session/use-me";

type SetupGoal = NonNullable<SetupDraftIn["goals"]>[number];

/** Wizard answers persisted in `workspace.onboarding.data` (PATCH /v1/workspace). */
export const onboardingDataSchema = z
  .object({
    template_key: z.string().optional(),
    /** Free-text "describe what you want" (step 1) — input for the AI setup draft. */
    description: z.string().optional(),
    /** Step 3 asks the AI for the first draft ("Generate my setup"). */
    generate: z.boolean().optional(),
    /** Agent created from the confirmed setup (step 3). */
    agent_id: z.string().optional(),
    agent_name: z.string().optional(),
    /** Step 2 Q&A pairs; `source_id` once a pair has been saved as a Knowledge Base source. */
    qa: z.array(z.object({ question: z.string(), answer: z.string(), source_id: z.string().optional() })).optional(),
    assistant: z
      .object({
        name: z.string(),
        purpose: z.string(),
        tone: z.string(),
        languages: z.array(z.string()),
        instructions: z.string(),
        strict_grounding: z.boolean(),
        fallback_message: z.string(),
      })
      .partial()
      .optional(),
    channels: z.record(z.boolean()).optional(),
    completed: z.boolean().optional(),
  })
  .passthrough();

export type OnboardingData = z.infer<typeof onboardingDataSchema>;

export function readOnboarding(workspace: WorkspaceOut | undefined): { step: number; data: OnboardingData } {
  const raw = (workspace?.onboarding ?? {}) as { step?: unknown; data?: unknown };
  const parsed = onboardingDataSchema.safeParse(raw.data ?? {});
  const step = typeof raw.step === "number" ? raw.step : 1;
  return { step, data: parsed.success ? parsed.data : {} };
}

export const NEW_WORKSPACE_PARAM = "new";

/** Workspace being set up: `?ws=<slug>`, else the last owned one; `?new=1` (or none owned) = create. */
function pickWorkspace(workspaces: readonly WorkspaceSummary[], params: URLSearchParams): WorkspaceSummary | undefined {
  if (params.has(NEW_WORKSPACE_PARAM)) return undefined;
  const owned = workspaces.filter((w) => w.membership_status === "active" && w.roles.includes("owner"));
  const slug = params.get("ws");
  return slug ? owned.find((w) => w.slug === slug) : owned.at(-1);
}

export function useOnboardingWorkspace() {
  const api = useApi();
  const me = useMe();
  const [params] = useSearchParams();
  const summary = me.data ? pickWorkspace(me.data.workspaces, params) : undefined;
  if (summary) setActiveWorkspaceId(summary.id);
  const details = useQuery({
    queryKey: queryKeys.workspace(summary?.id ?? "none"),
    queryFn: () => unwrap(api.GET("/v1/workspace")),
    enabled: Boolean(summary),
  });
  return {
    isPending: me.isPending || (Boolean(summary) && details.isPending),
    summary,
    workspace: details.data,
  };
}

/** `?step=` (1-based) ⇄ stepper index. */
export function useStepParam(): [number, (index: number) => void] {
  const [params, setParams] = useSearchParams();
  const raw = Number(params.get("step") ?? "1");
  const index = Number.isInteger(raw) ? Math.min(Math.max(raw - 1, 0), ONBOARDING_STEPS.length - 1) : 0;
  const setIndex = useCallback(
    (next: number) => {
      setParams(
        (prev) => {
          const copy = new URLSearchParams(prev);
          copy.set("step", String(next + 1));
          return copy;
        },
        { replace: false },
      );
      try {
        window.scrollTo({ top: 0, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
      } catch {
        // Scrolling is cosmetic (jsdom / embedded browsers).
      }
    },
    [setParams],
  );
  return [index, setIndex];
}

/** Workspace fields step 1 may change alongside the wizard position. */
export interface WorkspacePatch {
  name?: string;
  website?: string | null;
  city?: string | null;
  timezone?: string;
  default_language?: string;
  industry?: string | null;
  industry_label?: string | null;
  goals?: SetupGoal[];
  profile?: Record<string, string | string[]>;
  /** The starting template picked in step 1; its follow-up questions join the industry's. */
  template_key?: IndustryTemplateKey;
}

/** Persists the wizard position + answers on the workspace (`onboarding: {step, data}`). */
export function useSaveProgress(workspace: WorkspaceOut | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useCallback(
    async (step: number, patch: Partial<OnboardingData>, profile: WorkspacePatch = {}) => {
      if (!workspace) return;
      const data = { ...readOnboarding(workspace).data, ...patch };
      const updated = await unwrap(api.PATCH("/v1/workspace", { body: { ...profile, onboarding: { step, data } } }));
      queryClient.setQueryData(queryKeys.workspace(workspace.id), updated);
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    },
    [api, queryClient, workspace],
  );
}
